import { useState, useEffect, useCallback, useMemo } from 'react';
import { Agent } from '../types';
import { apiService, ApiError, BackendAgent } from '../services/api';
import { getActiveMockProfile, isMockProfileEnabled } from '../utils/mockProfiles';
import { getMockAgentsResponse, isMockAgentsEnabled } from '../utils/mockAgents';

// Convert backend agent format to frontend agent format
const convertBackendAgent = (backendAgent: BackendAgent): Agent => {
  // Map agent IDs to colors and icons (you can customize these)
  // Using optimized 128px images for better clarity at small display sizes
  const agentConfig: Record<string, { color: string; icon: string; description: string; welcomeMessage?: string; isStreaming?: boolean; isFast?: boolean; capabilityTags?: string[] }> = {
    'aldo': {
      color: 'blue',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'Your UniFirst guide to AI agent navigation',
      capabilityTags: ['AI Assistant', 'Multi-Domain'],
      welcomeMessage: `Hi! I'm **Aldo**, your UniFirst AI concierge.

I'm here to help you with all your workplace questions — from HR policies and procedures to product search and general guidance.

---

**I can assist you with:**

- **HR Questions** — Policies, benefits, time off, workplace guidelines
- **Product Search** — Find uniforms, safety gear, and apparel
- **Documentation** — System procedures, training materials
- **Smart Routing** — I'll connect you with specialized agents when needed

---

Feel free to ask me anything! I'm designed to understand your needs and either provide direct assistance or route you to the most appropriate specialist.

**What can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'orientation': {
      color: 'cyan',
      icon: '/assets/logos/Aldoorientation-128.png',
      description: 'New hire orientation and onboarding assistance',
      capabilityTags: ['AI Specialist', 'New Hire Support'],
      welcomeMessage: `Welcome to UniFirst! I'm your **Orientation** agent.

I'm here to help you get started with everything you need to know as a new team member.

---

**I can help you with:**

- **Onboarding Steps** — What to expect in your first days and weeks
- **Benefits Enrollment** — Healthcare, retirement, and other benefit information
- **Company Culture** — UniFirst values, policies, and workplace expectations
- **Resources & Contacts** — Who to reach out to for different needs

---

**Try asking:**
> "How do I enroll in benefits?"
> "Tell me about UniFirst's company culture"
> "What software do I need to sign up for when starting out?"

**What would you like to know about UniFirst?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'direct_embroidery': {
      color: 'red',
      icon: '/assets/logos/AldoOwensboro-2-128.png',
      description: 'Owensboro production and direct embroidery assistance',
      capabilityTags: ['Production', 'Embroidery', 'Training'],
      welcomeMessage: `Hello! I'm your **Owensboro Production** agent.

I'm here to help you with all aspects of embroidery machine operation and garment production at Owensboro.

---

**I can assist with:**

- **Machine Operation** — Setup, adjustments, and troubleshooting
- **Production Techniques** — Hooping, stabilization, and quality control
- **Materials Guidance** — Thread selection, needle types, and fabric handling
- **Visual References** — Images of machine parts and techniques when available

---

**Try asking:**
> "How do I rethread the Tajima machine?"
> "Which stabilizer should I use on lightweight polos?"
> "How do I troubleshoot thread breaks?"

**What production question can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'O2_direct_embroidery': {
      color: 'red',
      icon: '/assets/logos/AldoOwensboro-2-128.png',
      description: 'Owensboro production and direct embroidery assistance',
      capabilityTags: ['Production', 'Embroidery', 'Training'],
      welcomeMessage: `Hello! I'm your **Owensboro Production** agent.

I'm here to help you with all aspects of embroidery machine operation and garment production at Owensboro.

---

**I can assist with:**

- **Machine Operation** — Setup, adjustments, and troubleshooting
- **Production Techniques** — Hooping, stabilization, and quality control
- **Materials Guidance** — Thread selection, needle types, and fabric handling
- **Visual References** — Images of machine parts and techniques when available

---

**Try asking:**
> "How do I rethread the Tajima machine?"
> "Which stabilizer should I use on lightweight polos?"
> "How do I troubleshoot thread breaks?"

**What production question can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'pwms-support': {
      color: 'icon-orange',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'Production and warehouse management system support',
      capabilityTags: ['AI Specialist', 'PWMS Expert'],
      welcomeMessage: `Hello! I'm your **PWMS Support** agent.

I'm here to help with PWMS (Personalization and Workplace Management System) support and workflow assistance.

---

**I can help you with:**

- **PWMS System Support** — Technical assistance and troubleshooting
- **Design Workflows** — Logo approvals, personalization features
- **System Navigation** — Help using PWMS features and functionality
- **Issue Resolution** — Support for PWMS-related problems

---

**Try asking:**
> "How do I create a new design in PWMS?"
> "What does the red warning mean when approving?"
> "How do I route a job through PWMS?"

**What PWMS question can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'product': {
      color: 'green',
      icon: '/assets/logos/aldo__product-128.png',
      description: 'Product catalog search, comparisons, and recommendations',
      capabilityTags: ['AI Specialist', 'Product Expert'],
      // welcomeMessage intentionally omitted — sourced from agents.json's
      // `introMessage` field via convertBackendAgent below. Edit the canonical
      // copy in apps/express-api/config/agents.json, not here.
    },
    'product_experimental': {
      color: 'green',
      icon: '/assets/logos/aldo__product-128.png',
      description: 'Hybrid product agent (experimental): single LLM + deterministic catalog tools',
      capabilityTags: ['Experimental', 'Product Expert'],
      welcomeMessage: `Hello! I'm your **Product (Experimental)** agent.

I'm a hybrid product assistant: I use a single AI model for conversation, and call deterministic tools for catalog search, filtering, industry top-10, and related products.

**Try asking:**
> "Show me hi-vis jackets for construction crews"
> "What are the top products for automotive?"
> "Show me products related to style code 0101"

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'unifirst_docs': {
      color: 'green',
      icon: '/assets/logos/aldounifirstdocs2-128.png',
      description: 'Processes, software help, forms & protocols from UniFirstDocs.com',
      capabilityTags: ['AI Specialist', 'Documentation Expert'],
      welcomeMessage: `Hello! I'm your **UniFirst Docs** agent.

I'm here to help you find answers from UniFirst internal documentation at [UniFirstDocs.com](https://unifirstdocs.unifirst.com/).

---

**I can help you with:**

- **Processes & Procedures** — Step-by-step instructions for any task
- **Software Help** — TRACS, ABS Desktop, Route Assistant, Oracle, and more
- **Forms & Templates** — Downloadable forms and document templates
- **Protocols & Policies** — Official guidelines and procedures

---

**Tip:** Use the settings widget in the top right to adjust image sizes or hide them.

**What can I help you find?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'service_desk': {
      color: 'purple',
      icon: '/assets/logos/aldo-service-desk-128.png',
      description: 'Ivanti IT Service Desk documentation and support',
      capabilityTags: ['AI Specialist', 'IT Support'],
      welcomeMessage: `Hello! I'm your **Service Desk** assistant.

I'm here to help you navigate Ivanti IT Service Management (ITSM) — from submitting tickets to managing your dashboard.

---

**I can help you with:**

- **Ticket Submission** — How to create and submit IT support tickets
- **Dashboard Navigation** — Understanding your Ivanti dashboard tiles and features
- **Analyst Workflows** — Ticket management and incident handling for analysts
- **Self-Service** — End-user self-service portal features and capabilities

---

**Try asking:**
> "How do I submit a ticket in Ivanti?"
> "What's on my Ivanti dashboard?"
> "How do I manage tickets as an analyst?"
> "What self-service features are available?"

**What can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'aldo-unified-agent': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'Legacy UniFirst AI assistant (earlier version).',
      capabilityTags: ['Legacy Agent']
    },
    'hr-policy-agent-2-filtering': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'HR policy agent with enhanced filtering (test version).',
      capabilityTags: ['Test Agent', 'HR Expert']
    },
    'hr-policy-agent-1': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'HR policy agent for company HR policies, procedures, and employee guidelines.',
      capabilityTags: ['AI Specialist', 'HR Expert'],
      welcomeMessage: `Welcome to the HR Policy Agent 1!

I'm here to help you quickly find answers to questions about company policies, benefits, time off, workplace guidelines, and more.

I can help you with:
- Company policies and procedures
- Employee benefits and time off
- HR guidelines and workplace rules
- Policy interpretations and clarifications

This AI tool is powered by generative AI, which means it can provide helpful responses based on company-approved documents and information. While we encourage responsible use, please remember:

- Avoid sharing sensitive personal information.
- Use your judgment—when in doubt, double-check with HR or your manager.
- Treat this assistant as a helpful guide, not a final authority. Some answers may be summaries or interpretations and may not reflect recent policy changes.

By using this assistant, you agree to follow our guidelines for safe, secure, and respectful use of AI.

Let's get started—what can I help you with today?

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'hr-policy-test-agent-citations-6': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'HR policy test agent with citation capabilities (test version).',
      capabilityTags: ['Test Agent', 'Citations']
    },
    'hr-policy-agent-7-streaming': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'HR policy streaming agent (test version).',
      capabilityTags: ['Test Agent', 'Streaming']
    },
    'hrpolicy': {
      color: 'icon-teal',
      icon: '/assets/logos/Aldohrpolicy-128.png',
      description: 'HR policy interpretation and guidance',
      capabilityTags: ['AI Specialist', 'HR Expert'],
      welcomeMessage: `Hello! I'm your **PolicyTech** agent.

I'm here to help you with UniFirst HR policies, procedures, and workplace guidelines. I have direct access to our comprehensive policy database and can provide you with accurate, up-to-date information.

---

**I can help you with:**

- **Company Policies** — Detailed policy information with source citations 
- **Workplace Guidelines** — Dress code, conduct, safety procedures
- **Time Off & Leave** — Vacation, sick leave, FMLA, bereavement policies

---

>  **Important:** Avoid sharing sensitive personal information. When in doubt, consult with HR or your manager for clarification.

---

**Try asking:**
> "What are the company's vacation policies?"
> "How do I report a workplace injury?"
> "What's the dress code policy?"

**What policy question can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    },
    'hr-policy-agent-webapp': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'PolicyTech HR agent (earlier version).',
      capabilityTags: ['Legacy Agent']
    },
    'ai-policy-agent': {
      color: 'icon-teal',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'AI governance and policy assistant (test version).',
      capabilityTags: ['Test Agent', 'AI Governance']
    },
    'firstaid-safety': {
      color: 'red',
      icon: '/assets/logos/aldo-no-back-128.png',
      description: 'NFPA 10 fire extinguisher standards and compliance guidance',
      capabilityTags: ['AI Specialist', 'Safety Expert'],
      welcomeMessage: `Hello! I'm your **First Aid & Safety** agent.

I specialize in **NFPA 10 – Standard for Portable Fire Extinguishers (2022 Edition)**. I can help you understand fire extinguisher requirements, inspection schedules, maintenance procedures, and compliance standards.

---

**I can help you with:**

- **Inspection Requirements** — Monthly and annual inspection procedures
- **Maintenance & Recharging** — When and how to service extinguishers
- **Hydrostatic Testing** — Testing intervals and requirements by extinguisher type
- **Installation & Placement** — Travel distances, mounting heights, signage
- **Classifications** — Class A, B, C, D, K extinguisher requirements
- **Compliance** — Obsolete extinguishers, recordkeeping, documentation

---

**Try asking:**
> "How often do fire extinguishers need to be inspected?"
> "What are the hydrostatic testing requirements?"
> "What is the travel distance requirement for fire extinguishers?"

**What fire safety question can I help you with today?**

---

> Please use the thumbs up/down buttons below to report whether or not a response is helpful. This helps make improvement possible!`
    }
  };

  const config = agentConfig[backendAgent.id] || {
    color: 'icon-teal',
    icon: '/assets/logos/aldo-no-back-128.png',
    description: 'AI Assistant'
  };

  return {
    id: backendAgent.id,
    name: backendAgent.name,
    description: backendAgent.description || config.description,
    icon: config.icon,
    color: config.color,
    isActive: true,
    type: 'official',
    hidden: backendAgent.hidden === true,
    // Prefer introMessage from agents.json (definitive source), fall back to hardcoded welcomeMessage
    welcomeMessage: backendAgent.introMessage || config.welcomeMessage || '',
    // Optional condensed welcome for returning users (no local-config fallback)
    ...(backendAgent.shortIntroMessage && { shortWelcomeMessage: backendAgent.shortIntroMessage }),
    ...(backendAgent.shortIntroMessage && { shortIntroMessage: backendAgent.shortIntroMessage }),
    ...(backendAgent.projectContactEmail && { projectContactEmail: backendAgent.projectContactEmail }),
    ...(backendAgent.isStreaming !== undefined && { isStreaming: backendAgent.isStreaming }),
    ...(config.isFast && { isFast: config.isFast }),
    ...(config.capabilityTags && { capabilityTags: config.capabilityTags }),
    // Pass through new fields from backend agents.json
    ...(backendAgent.version && { version: backendAgent.version }),
    ...(backendAgent.category && { category: backendAgent.category }),
    ...(backendAgent.sampleQuestions && { sampleQuestions: backendAgent.sampleQuestions }),
    ...(backendAgent.isNew !== undefined && { isNew: backendAgent.isNew }),
    ...(backendAgent.supportsFastMode !== undefined && { supportsFastMode: backendAgent.supportsFastMode }),
    // New agents.json fields
    ...(backendAgent.featured !== undefined && { featured: backendAgent.featured }),
    ...(backendAgent.introMessage && { introMessage: backendAgent.introMessage }),
    ...(backendAgent.routingTips && { routingTips: backendAgent.routingTips }),
    // Release lifecycle (from agents.json — single source of truth)
    ...(backendAgent.releaseDate && { releaseDate: backendAgent.releaseDate }),
    ...(backendAgent.demoDate && { demoDate: backendAgent.demoDate }),
    ...(backendAgent.uatStartDate && { uatStartDate: backendAgent.uatStartDate }),
    ...(backendAgent.uatEndDate && { uatEndDate: backendAgent.uatEndDate }),
    ...(backendAgent.lastUpdated && { lastUpdated: backendAgent.lastUpdated }),
    ...(backendAgent.versionHistory && { versionHistory: backendAgent.versionHistory }),
    ...(backendAgent.updateNotice && { updateNotice: backendAgent.updateNotice }),
    // Contacts
    ...(backendAgent.contacts && { contacts: backendAgent.contacts }),
    // Work items
    ...(backendAgent.workItems && { workItems: backendAgent.workItems }),
    // Access channels (alternative surfaces like embedded widgets)
    ...(backendAgent.accessChannels && { accessChannels: backendAgent.accessChannels as Agent['accessChannels'] }),
  };
};

export const useAgents = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [userProfile, setUserProfile] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [backendConnected, setBackendConnected] = useState<boolean>(false);

  const isLocalhostHost = () => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h.startsWith('192.168.') || h.endsWith('.local');
  };

  // Load agents from backend. `isRetry` marks the one automatic retry we allow
  // after a 401 — a stale/expired auth token on the very first call after page
  // load can cause a single spurious 401 before silent token renewal catches
  // up; without a retry the agent list would stay empty until a manual reload.
  const loadAgents = useCallback(async (isRetry: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      // Local dev escape hatch: when the mock-agents flag is on, skip the API
      // entirely. This lets UI work happen without a reachable backend or OIDC.
      if (isMockAgentsEnabled()) {
        console.log('[useAgents] mockAgents flag active — loading mock agent list');
        const response = getMockAgentsResponse();
        setAgents(response.agents.map(convertBackendAgent));
        if (response.user) setUserProfile(response.user);
        setBackendConnected(false);
        return;
      }

      // First check if backend is healthy
      await apiService.healthCheck();
      setBackendConnected(true);

      // Get agents from backend
      console.log('Loading agents from backend');
      const response = await apiService.getAgents();

      const convertedAgents = response.agents.map(convertBackendAgent);

      // Auto-fallback: if running on localhost and the backend returned no
      // agents (typically because OIDC auth didn't complete against the
      // remote dev API), drop into the mock list so the UI is still usable.
      if (convertedAgents.length === 0 && isLocalhostHost()) {
        console.warn(
          '[useAgents] backend returned 0 agents on localhost — falling back to mock agents so the UI is not blank. Enable permanently with window.askAldo.mockAgents.enable()',
        );
        const mockResponse = getMockAgentsResponse();
        setAgents(mockResponse.agents.map(convertBackendAgent));
        if (mockResponse.user) setUserProfile(mockResponse.user);
        return;
      }

      setAgents(convertedAgents);

      // Store user profile if provided
      if (response.user) {
        console.log('User profile received:', response.user);
        setUserProfile(response.user);
      }

    } catch (err) {
      console.error('Failed to load agents:', err);
      setBackendConnected(false);

      // Auto-fallback on localhost: network/401 errors should not leave the
      // dev UI completely empty.
      if (isLocalhostHost()) {
        console.warn(
          '[useAgents] backend call failed on localhost — loading mock agents. Run window.askAldo.mockAgents.disable() to opt out once the backend is reachable.',
        );
        const mockResponse = getMockAgentsResponse();
        setAgents(mockResponse.agents.map(convertBackendAgent));
        if (mockResponse.user) setUserProfile(mockResponse.user);
        setError(null);
        return;
      }

      // A 401 on the very first load is almost always a stale/expiring auth
      // token racing page load, not a real permissions problem — retry once
      // after a short delay (by then silent token renewal has usually caught
      // up) instead of leaving the user with a permanently empty agent list.
      if (!isRetry && err instanceof ApiError && err.status === 401) {
        console.warn('[useAgents] got 401 on initial load — retrying once in 1.5s (likely a stale auth token)');
        setTimeout(() => loadAgents(true), 1500);
        return;
      }

      if (err instanceof ApiError) {
        if (err.isNetworkError()) {
          setError('Unable to connect to the backend server. Please check your internet connection and try again.');
        } else {
          setError(`Backend error: ${err.message}`);
        }
      } else {
        setError('An unexpected error occurred while loading agents');
      }

      // Fallback to empty agents array on error
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reload agents
  const reloadAgents = useCallback(() => {
    loadAgents();
  }, [loadAgents]);

  // Check specific agent health
  const checkAgentHealth = useCallback(async (agentId: string) => {
    try {
      const health = await apiService.checkAgentHealth(agentId);
      return health;
    } catch (err) {
      console.error(`Agent ${agentId} health check failed:`, err);
      return null;
    }
  }, []);

  // Load agents on mount
  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  // Mock profile info for UI indicators (filtering is now done server-side via RBAC)
  const mockProfile = useMemo(() => getActiveMockProfile(), []);
  const isMockEnabled = useMemo(() => isMockProfileEnabled(), []);

  if (isMockEnabled && mockProfile) {
    console.log(`🧪 Mock Profile Active: "${mockProfile.name}" — roles: [${mockProfile.roles.join(', ')}] — backend RBAC filtering ${agents.length} agents`);
  }

  return {
    agents,
    allAgents: agents,
    userProfile,
    loading,
    error,
    backendConnected,
    reloadAgents,
    checkAgentHealth,
    mockProfile,
    isMockProfileEnabled: isMockEnabled
  };
}; 